// Plano de preventiva do contrato: linhas (sistemas + parques + frequência) e as visitas deste mês e do próximo.
// O lote do mês cria as OS "a agendar" com data sugerida; o operador põe a data real na Agenda.
import { useState } from 'react';
import { Link } from 'react-router';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '../../api';
import { FREQUENCIAS, SISTEMAS, dataBr, deFreq, paraFreq, type LinhaPlano, type Visita } from './comum';

export interface PlanoFicha {
  linhas: (LinhaPlano & { id: string; frequencia: string; versao: number; parques_cobertos: number })[];
  mes_atual: { competencia: string; nome: string; visitas: Visita[] };
  proximo_mes: { competencia: string; nome: string; visitas: Visita[] };
}
interface Edicao { id?: string; nome: string; sistemas: string[]; local_ids: string[]; freq: string; mes_referencia: string; duracao_min: string; observacao: string; ativo: boolean }

const NOME_STATUS: Record<string, string> = { aberta: 'a agendar', agendada: 'agendada', em_deslocamento: 'a caminho', em_execucao: 'em execução', pausada: 'pausada', concluida: 'concluída', cancelada: 'cancelada' };
const paraEdicao = (l: PlanoFicha['linhas'][number]): Edicao => ({
  id: l.id, nome: l.nome, sistemas: l.sistemas, local_ids: l.local_ids ?? [], freq: paraFreq(l.vezes_mes, l.a_cada_meses),
  mes_referencia: (l.mes_referencia ?? '').slice(0, 7), duracao_min: l.duracao_min ? String(l.duracao_min) : '', observacao: l.observacao ?? '', ativo: l.ativo !== false,
});

export function PlanoContrato({ contratoId, plano, locais, podeEditar, podeAgendar, classificado, aoMudar }: {
  contratoId: string; plano: PlanoFicha; locais: { id: string; nome: string }[]; podeEditar: boolean; podeAgendar: boolean; classificado: boolean; aoMudar: (msg?: string) => void;
}) {
  const [edicao, setEdicao] = useState<Edicao[] | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const ativas = plano.linhas.filter((l) => l.ativo !== false);

  async function salvar() {
    setErro(null); setSalvando(true);
    try {
      await api(`/contratos/${contratoId}/plano`, { metodo: 'PUT', corpo: { linhas: edicao!.map((e) => ({
        ...(e.id ? { id: e.id } : {}), nome: e.nome.trim(), sistemas: e.sistemas, local_ids: e.local_ids.length ? e.local_ids : null, ...deFreq(e.freq),
        mes_referencia: e.freq.startsWith('m') && e.mes_referencia ? e.mes_referencia : null, duracao_min: e.duracao_min ? Number(e.duracao_min) : null,
        observacao: e.observacao.trim() || null, ativo: e.ativo,
      })) } });
      setEdicao(null); aoMudar('Plano de preventiva salvo.');
    } catch (e) { setErro((e as Error).message); } finally { setSalvando(false); }
  }
  async function gerar(competencia: string) {
    setErro(null);
    try {
      const r = await api<{ criadas: number }>('/preventivas/gerar', { metodo: 'POST', corpo: { competencia: competencia.slice(0, 7) } });
      aoMudar(r.criadas ? `${r.criadas} OS preventiva(s) criada(s) na fila "A agendar".` : 'Nada novo para criar neste mês.');
    } catch (e) { setErro((e as Error).message); }
  }
  const muda = (i: number, p: Partial<Edicao>) => setEdicao(edicao!.map((x, j) => (j === i ? { ...x, ...p } : x)));

  return (
    <section className="secao">
      <div className="secao-topo">
        <h2>Plano de preventiva</h2>
        {podeEditar && !edicao && <button className="btn-fantasma" onClick={() => setEdicao(plano.linhas.map(paraEdicao))}>Editar plano</button>}
        {edicao && <div className="botoes"><button className="btn-fantasma" onClick={() => { setEdicao(null); setErro(null); }}>Cancelar</button>
          <button className="btn" onClick={salvar} disabled={salvando || edicao.some((e) => e.nome.trim().length < 2)}>{salvando ? 'Salvando…' : 'Salvar plano'}</button></div>}
      </div>
      <p className="nota">Cada linha gera, todo mês, uma OS "a agendar" por parque e visita, com data sugerida. Só entram os parques que o contrato cobre para aqueles sistemas. O lote do mês seguinte sai a partir do dia 20.</p>
      {erro && <p className="alerta" role="alert">{erro}</p>}
      {!classificado && <p className="alerta alerta-aviso">Contrato sem tipo não gera preventiva: classifique o Anexo I abaixo.</p>}
      {!edicao && ativas.some((l) => l.parques_cobertos === 0) && <p className="alerta alerta-aviso">Há linha do plano que não gera visita: os sistemas dela (ou os parques escolhidos) não estão nos "sistemas cobertos" do Anexo I. Inclua o sistema na cobertura ou ajuste a linha.</p>}

      {!edicao && (ativas.length === 0 ? <div className="cartao vazio"><p>Sem plano de preventiva.</p></div> : (
        <div className="cartao tabela-cartao rolagem-x"><table className="tabela">
          <thead><tr><th>Linha</th><th>Sistemas</th><th>Parques</th><th>Frequência</th><th>Duração</th></tr></thead>
          <tbody>{ativas.map((l) => (
            <tr key={l.id}><td><strong>{l.nome}</strong>{l.origem === 'legado' && <small>vinda do campo antigo "visitas por mês"</small>}{l.origem === 'documento' && <small>lida do contrato escaneado</small>}</td>
              <td>{l.sistemas.length ? l.sistemas.map((s) => SISTEMAS[s]).join(', ') : 'Todos'}</td>
              <td>{l.local_ids?.length ? locais.filter((x) => l.local_ids!.includes(x.id)).map((x) => x.nome).join(', ') : 'Todos os cobertos'}
                {l.parques_cobertos === 0 && <span className="situacao s-nok">não gera: sistema fora da cobertura do contrato</span>}</td>
              <td>{l.frequencia}{l.a_cada_meses > 1 && l.mes_referencia ? <small>a partir de {dataBr(l.mes_referencia).slice(3)}</small> : null}</td>
              <td>{l.duracao_min ? `${l.duracao_min} min` : '—'}</td></tr>
          ))}</tbody>
        </table></div>
      ))}

      {edicao && (
        <div className="cartao form">
          {edicao.map((e, i) => (
            <div key={e.id ?? `n${i}`} className={`linha-plano-edicao${e.ativo ? '' : ' inativa'}`}>
              <div className="linha-3-igual">
                <label>Nome<input value={e.nome} onChange={(x) => muda(i, { nome: x.target.value })} placeholder="ex.: CFTV, Cerca e alarme" maxLength={80} /></label>
                <label>Frequência<select value={e.freq} onChange={(x) => muda(i, { freq: x.target.value })}>{FREQUENCIAS.map(([f, r]) => <option key={f} value={f}>{r}</option>)}</select></label>
                {e.freq.startsWith('m')
                  ? <label>A partir do mês<input type="month" value={e.mes_referencia} onChange={(x) => muda(i, { mes_referencia: x.target.value })} /></label>
                  : <label>Duração prevista (min)<input inputMode="numeric" value={e.duracao_min} onChange={(x) => muda(i, { duracao_min: x.target.value.replace(/\D/g, '') })} placeholder="opcional" /></label>}
              </div>
              <span className="rotulo">Sistemas (nenhum = todos)</span>
              <div className="chips">{Object.entries(SISTEMAS).map(([s, r]) => (
                <button type="button" key={s} className={`chip${e.sistemas.includes(s) ? ' ativo' : ''}`} onClick={() => muda(i, { sistemas: e.sistemas.includes(s) ? e.sistemas.filter((y) => y !== s) : [...e.sistemas, s] })}>{r}</button>
              ))}</div>
              {locais.length > 1 && <>
                <span className="rotulo">Parques (nenhum = todos os cobertos)</span>
                <div className="chips">{locais.map((l) => (
                  <button type="button" key={l.id} className={`chip${e.local_ids.includes(l.id) ? ' ativo' : ''}`} onClick={() => muda(i, { local_ids: e.local_ids.includes(l.id) ? e.local_ids.filter((y) => y !== l.id) : [...e.local_ids, l.id] })}>{l.nome}</button>
                ))}</div>
              </>}
              <div className="linha-acoes">
                <input value={e.observacao} onChange={(x) => muda(i, { observacao: x.target.value })} placeholder="Observação (opcional)" maxLength={500} />
                {e.id ? <label className="check"><input type="checkbox" checked={e.ativo} onChange={(x) => muda(i, { ativo: x.target.checked })} /> ativa</label>
                  : <button type="button" className="btn-fantasma perigo" onClick={() => setEdicao(edicao.filter((_, j) => j !== i))} aria-label="Remover linha"><Trash2 size={15} aria-hidden /></button>}
              </div>
            </div>
          ))}
          <button type="button" className="btn-fantasma" onClick={() => setEdicao([...edicao, { nome: '', sistemas: [], local_ids: [], freq: 'v1', mes_referencia: '', duracao_min: '', observacao: '', ativo: true }])}><Plus size={16} aria-hidden /> Linha</button>
          <small className="ajuda">Linha com OS já gerada não é apagada: desmarque "ativa" para parar de gerar.</small>
        </div>
      )}

      {!edicao && [plano.mes_atual, plano.proximo_mes].map((m) => m.visitas.length > 0 && (
        <div key={m.competencia} className="bloco-mes">
          <div className="secao-topo">
            <h3 className="subtitulo">Visitas de {m.nome}</h3>
            {podeAgendar && m.visitas.some((v) => !v.os_id && !v.pulada) && <button className="btn-fantasma" onClick={() => gerar(m.competencia)}>Gerar as OS de {m.nome}</button>}
          </div>
          <div className="cartao tabela-cartao rolagem-x"><table className="tabela">
            <thead><tr><th>Linha</th><th>Parque</th><th>Visita</th><th>Data sugerida</th><th>OS</th></tr></thead>
            <tbody>{m.visitas.map((v) => (
              <tr key={`${v.plano_id}-${v.local_id}-${v.visita}`} className={v.pulada ? 'inativo' : ''}>
                <td>{v.plano}</td><td>{v.local}</td><td>{v.de > 1 ? `${v.visita} de ${v.de}` : '—'}</td>
                <td>{dataBr(v.data_sugerida)}</td>
                <td>{v.os_id ? <><Link to={`/os/${v.os_id}`}>{v.os_numero}</Link><small>{v.agendada_para ? `agendada ${new Date(v.agendada_para).toLocaleDateString('pt-BR')}` : NOME_STATUS[v.os_status ?? ''] ?? v.os_status}</small></>
                  : v.pulada ? <small>{v.pulada}</small> : <small>ainda não gerada</small>}</td>
              </tr>
            ))}</tbody>
          </table></div>
        </div>
      ))}
    </section>
  );
}
